import React, { useState, useContext, useEffect } from "react";
import AsyncSelect from "react-select/async";
import { Modal } from "react-bootstrap";
import { Button, IconButton, InputAdornment } from "@mui/material";
import LoadingButton from "@mui/lab/LoadingButton";
import { Visibility, VisibilityOff } from "@mui/icons-material";
import { TextField } from "@mui/material";
import toast from "react-hot-toast";
import blogFetch from "../../axios/config";
import { IAddUser, IModalAddUserProps } from "./UserGroupsInterfaces";
import { getIdCompany } from "../../utils/constantes";
import Context from "../../context/Context";
import { ISector } from "../occurrence/occurrenceInterfaces";
import DataText from "./nls/pt-BR.json"
import language from "../../nls/language.json";

const USER_INITIAL: IAddUser = {
  user_CompanyId: getIdCompany(),
  user_Password: "",
  user_UserName: "",
  User_Email: "",
  User_Matricula: 0,
  User_SAPMatricula: 0,
  User_Active: true,
  user_Sector: null
}

const ModalAddUser: React.FC<IModalAddUserProps> = ({
  open,
  onClose,
  getUser,
}) => {
  const [loading, setLoading] = useState<boolean>(false);
  
  const [user, setUser] = useState<IAddUser>(USER_INITIAL);
  const [passwordsMatch, setPasswordsMatch] = useState<boolean>(true);
  const [confirmPassword, setConfirmPassword] = useState<string>("");
  const [selectSector, setSelectSector] = useState<ISector>();
  const [showPassword, setShowPassword] = useState({
    password: true,
    confirmPassword: true
  });

  const {
    getSector,
    selectedCountry
} = useContext(Context);

  const handlePasswordVisibility = ({ name, value }: { name: string, value: boolean }) => {
    setShowPassword({
      ...showPassword,
      [name]: !value
    });
  };

  const addUser = async (user: IAddUser) => {
    try {
      if (user.user_Password !== confirmPassword) {
        setPasswordsMatch(false);
        return;
      }
      setLoading(true);
      
      const response = await blogFetch.post(`/User`, user);
      const data = response.data;
      setUser(USER_INITIAL);
      await getUser();
      toast.success(DataText.user.register.success, {
        duration: 4000
      });
      onClose();
      return data;
    } catch (error) {
      console.error(error);
      toast.error(DataText.user.register.error, {
        duration: 4000
      });
    }
    finally {
      setLoading(false);
    }
  };

  const handleUser = ({ name, value }: { name: string, value: any }) => {
    setUser({
      ...user,
      [name]: value
    } as IAddUser);
  }

  return (
    <Modal show={open} onHide={onClose}>
      <Modal.Header closeButton>
        <Modal.Title>
          {(language as Record<string, any>)[selectedCountry.name].Permissoes.usuario.modal.adicionar.title}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <div>
          <TextField
            required
            className="mb-3"
            id="outlined-basic"
            label="Usuário"
            variant="outlined"
            size="small"
            fullWidth
            name="user_UserName"
            value={user.user_UserName}
            onChange={({ target: { name, value }}) => handleUser({ name, value })}
            sx={{ backgroundColor: "white", borderRadius: "5px" }}
          />
          <TextField
            className="mb-3"
            id="outlined-basic"
            label="Matricula RH"
            variant="outlined"
            size="small"
            fullWidth
            name="User_Matricula"
            value={user.User_Matricula ? user.User_Matricula : ""}
            onChange={({ target: { name, value }}) => handleUser({ name, value: Number(value) })}
            sx={{ backgroundColor: "white", borderRadius: "5px" }}
          />
          <TextField
            className="mb-3"
            id="outlined-basic"
            label="SAP"
            variant="outlined"
            size="small"
            fullWidth
            name="User_SAPMatricula"
            value={user.User_SAPMatricula ? user.User_SAPMatricula : ""}
            onChange={({ target: { name, value }}) => handleUser({ name, value: Number(value) })}
            sx={{ backgroundColor: "white", borderRadius: "5px" }}
          />
          <TextField
            className="mb-3"
            id="outlined-basic"
            label="Email"
            variant="outlined"
            size="small"
            fullWidth
            name="User_Email"
            value={user.User_Email}
            onChange={({ target: { name, value }}) => handleUser({ name, value })}
            sx={{ backgroundColor: "white", borderRadius: "5px" }}
          />
          <AsyncSelect
            required
            className="mb-3"
            cacheOptions
            defaultOptions
            placeholder={"Selecionar Setor"}
            value={selectSector}
            getOptionLabel={(e) => e.stR_description}
            getOptionValue={(e) => e.stR_description}
            loadOptions={getSector}
            onChange={(e) => handleUser({ name: "user_Sector", value: e?.stR_PK_idSector })}
          />
          <TextField
            required
            className="mb-3 z-index-0"
            id="outlined-basic"
            placeholder="Senha"
            variant="outlined"
            size="small"
            fullWidth
            type={!showPassword.password ? "text" : "password"}
            name="user_Password"
            value={user.user_Password}
            onChange={({ target: { name, value }}) => handleUser({ name, value })}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    onClick={() => handlePasswordVisibility({ name: "password", value: showPassword.password })}
                  >
                    {!showPassword.password ? <Visibility /> : <VisibilityOff />}
                  </IconButton>
                </InputAdornment>
              )
            }}
          />
          <TextField
            required
            id="outlined-basic"
            placeholder="Confirmar Senha"
            variant="outlined"
            size="small"
            fullWidth
            type={!showPassword.confirmPassword ? "text" : "password"}
            name="Confirmar Senha"
            value={confirmPassword}
            onChange={({ target: { value }}) => setConfirmPassword(value)}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    onClick={() => handlePasswordVisibility({ name: "confirmPassword", value: showPassword.confirmPassword })}
                  >
                    {!showPassword.confirmPassword ? <Visibility /> : <VisibilityOff />}
                  </IconButton>
                </InputAdornment>
              )
            }}
          />
          {passwordsMatch ? null : (
            <p style={{ color: "red", fontSize: "14px" }}>
              As senhas não coincidem.
            </p>
          )}
        </div>
      </Modal.Body>
      <Modal.Footer style={{ display: "flex", gap: 10 }}>
        <Button variant="text" onClick={onClose}>
          {(language as Record<string, any>)[selectedCountry.name].Permissoes.usuario.modal.adicionar.btnCancelar}
        </Button>
        {loading ? (
          <LoadingButton loading variant="outlined">
            Submit
          </LoadingButton>
        ) : (
          <Button
            variant="contained"
            onClick={() => addUser(user)}
            disabled={
              !passwordsMatch
              || !user.user_UserName
              || !user.User_Matricula
              || !user.user_Password
              || !user.user_Sector
            }
          >
            {(language as Record<string, any>)[selectedCountry.name].Permissoes.usuario.modal.adicionar.btnSalvar}
          </Button>
        )}
      </Modal.Footer>
    </Modal>
  );
};

export default ModalAddUser;
